import { ColumnDef } from '@tanstack/react-table';
import { InformationSection } from '@/components/common/InformationSection';
import { useEffect, useRef, useState } from 'react';
import { useHeader } from '@/components/context/HeaderContext';
import { useParams } from 'react-router-dom';
import { useGetContentCategoriesQuery } from '@/store/api/categoryApi';
import {
  useGetContentByIdQuery,
  useStreamContentQuery,
  useUpdateContentMutation,
} from '@/store/api/contentApi';
import { useUploadFileMutation } from '@/store/api/uploadApi';
import { getUnlockedCategoryPath, getUnlockedContentPath } from '@/utils/contentRoutes';
import FullScreenLoader from '@/components/common/FullScreenLoader';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { createContentSchema } from '@/utils/validation';
import type { UpdateContentRequest } from '@/types';

export type ContentData = {
  id: string;
  userEmail: string;
  avatarUrl: string;
  dateTime: string;
  duration: string;
  playCount: number;
};

export const columns: ColumnDef<ContentData>[] = [
  {
    accessorKey: 'user',
    header: 'User',
    cell: ({ row }) => (
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-[#1E0036] flex-shrink-0 overflow-hidden border border-[#F0EEF9]">
          <img
            src={row.original.avatarUrl || '/api/placeholder/40/40'}
            alt="User"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="font-semibold text-[16px] text-[#1E0036]">{row.original.userEmail}</div>
      </div>
    ),
  },
  {
    accessorKey: 'dateTime',
    header: 'Date & Time',
    cell: ({ row }) => <span className="font-bold text-[#1A1A1A]">{row.original.dateTime}</span>,
  },
  {
    accessorKey: 'duration',
    header: 'Duration',
    cell: ({ row }) => <span className="font-bold text-[#1A1A1A]">{row.original.duration}</span>,
  },
  {
    accessorKey: 'playCount',
    header: 'Play Count',
    cell: ({ row }) => (
      <div className="font-bold text-[#1A1A1A] text-center w-full max-w-[80px]">
        {row.original.playCount.toString().padStart(2, '0')}
      </div>
    ),
  },
];
const UnLockedContentDetail = () => {
  const { setHeader } = useHeader();
  const { categoryId, contentId } = useParams<{
    categoryId: string;
    contentId: string;
  }>();
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const [isSourceUploading, setIsSourceUploading] = useState(false);
  const [streamedAudioUrl, setStreamedAudioUrl] = useState<string | null>(null);
  const { data: categories = [], isLoading: isCategoriesLoading } =
    useGetContentCategoriesQuery(false);
  const contentIdNumber = Number(contentId);
  const { data: content, isLoading: isContentLoading } = useGetContentByIdQuery(contentIdNumber, {
    skip: Number.isNaN(contentIdNumber),
  });
  const { refetch: streamContent } = useStreamContentQuery(contentIdNumber, {
    skip: !contentIdNumber,
  });
  const category = categories.find((item) => String(item.id) === categoryId);
  const isMatchingUnlockedContent =
    !!content && content.category?.isLocked === false && String(content.categoryId) === categoryId;

  const [updateContent] = useUpdateContentMutation();
  const [uploadFile] = useUploadFileMutation();
  const sanitize = (value?: string) => value?.trim().replace(/\s+/g, ' ') ?? '';
  const sanitizeUrl = (value?: string) => {
    const sanitized = value?.trim() ?? '';
    return sanitized ? sanitized : null;
  };

  const resolveImageUrl = async (value: string | File | null | undefined) => {
    if (value instanceof File) {
      const uploadResponse = await uploadFile({ file: value, kind: 'image' }).unwrap();
      return uploadResponse.meta.imageUrl;
    }

    if (typeof value === 'string') {
      const sanitized = value.trim();
      return sanitized || null;
    }

    return null;
  };
  const uploadAudioSource = async (value: File) => {
    setIsSourceUploading(true);

    try {
      const uploadResponse = await uploadFile({ file: value }).unwrap();

      return {
        audioUrl: uploadResponse.meta.url || uploadResponse.meta.fileUrl || null,
        audioKey: uploadResponse.meta.audioKey || null,
        duration: uploadResponse.meta.duration ?? null,
        audioUploadToken: uploadResponse.meta.uploadToken || null,
      };
    } finally {
      setIsSourceUploading(false);
    }
  };
  const uploadVideoSource = async (value: File) => {
    setIsSourceUploading(true);

    try {
      const uploadResponse = await uploadFile({ file: value, kind: 'video' }).unwrap();

      return {
        videoUrl:
          (uploadResponse.meta as { videoUrl?: string }).videoUrl ||
          uploadResponse.meta.fileUrl ||
          uploadResponse.meta.url ||
          null,
        videoDuration: uploadResponse.meta.duration ?? null,
        uploadToken: uploadResponse.meta.uploadToken || null,
      };
    } finally {
      setIsSourceUploading(false);
    }
  };

  useEffect(() => {
    const saveButton = sectionRef.current?.querySelector<HTMLButtonElement>('button.bg-darkPurple');

    if (!saveButton) return;

    saveButton.textContent = isSourceUploading ? 'Uploading...' : 'Save';
    saveButton.disabled = isSourceUploading;
  }, [isSourceUploading]);

  useEffect(() => {
    if (!content || !isMatchingUnlockedContent) return;

    setHeader({
      type: 'content',
      props: {
        title: content.title,
        breadcrumbs: [
          { label: 'Home', href: '/' },
          { label: 'Open Content', href: '/open-content' },
          {
            label: category?.title || '',
            href: category ? getUnlockedCategoryPath(category.id) : '/open-content',
          },
          {
            label: content.title,
            href: category ? getUnlockedContentPath(category.id, content.id) : '/open-content',
          },
        ],
        onBack: () => window.history.back(),
        lastUpdated: '14-April-2026, 3:00PM',
      },
    });

    return () => setHeader(null);
  }, [category, content, isMatchingUnlockedContent, setHeader]);

  if (isCategoriesLoading || isContentLoading)
    return (
      <div className="p-6">
        <FullScreenLoader />
      </div>
    );
  if (!content || !isMatchingUnlockedContent)
    return (
      <div>
        <NotFoundPage />
      </div>
    );

  const hasAudioSource = Boolean(content.cachedAudioUrl || content.audioUrl || content.audioKey);
  const hasVideoSource = Boolean(content.videoUrl);
  const hasEmbedSource = Boolean(content.embedUrl);

  return (
    <div ref={sectionRef} className="p-3 sm:p-6 bg-[#F8F9FB] min-h-screen space-y-6">
      <div className="l h-full">
        <InformationSection
          title="Content Information"
          editable
          className="h-full bg-white"
          onSave={async (values) => {
            const imageUrl =
              values.imageUrl instanceof File
                ? await resolveImageUrl(values.imageUrl)
                : content.imageUrl;
            let shouldRefreshStream = false;
            const nextPayload: UpdateContentRequest = {
              id: content.id,
              title: sanitize(values.title) || content.title,
              description: values.description || content.description,
              imageUrl,
            };

            if (values.file instanceof File) {
              const audioUpload = await uploadAudioSource(values.file);

              nextPayload.audioUrl = audioUpload.audioUrl;
              nextPayload.audioKey = audioUpload.audioKey;
              nextPayload.duration = audioUpload.duration;
              nextPayload.audioUploadToken = audioUpload.audioUploadToken;
              shouldRefreshStream = true;
            }

            if (values.videoFile instanceof File) {
              const videoUpload = await uploadVideoSource(values.videoFile);

              nextPayload.videoUrl = videoUpload.videoUrl;
              nextPayload.videoDuration = videoUpload.videoDuration;
              nextPayload.uploadToken = videoUpload.uploadToken;
            }

            if (typeof values.embedUrl === 'string') {
              const nextEmbedUrl = sanitizeUrl(values.embedUrl);

              if (nextEmbedUrl && nextEmbedUrl !== content.embedUrl) {
                nextPayload.embedUrl = nextEmbedUrl;
              }
            }

            await updateContent(nextPayload).unwrap();
            if (shouldRefreshStream) {
              const streamResponse = await streamContent();

              if (streamResponse.data?.audioUrl) {
                setStreamedAudioUrl(streamResponse.data.audioUrl);
              }
            }
          }}
          validationSchema={createContentSchema(false).pick({
            title: true,
            category: true,
            description: true,
            imageUrl: true,
            embedUrl: true,
          })}
          fields={[
            {
              id: 'title',
              label: 'Title',
              defaultValue: content.title,
            },
            {
              id: 'sourceType',
              label: 'Source Type',
              defaultValue: content.sourceType || '',
              editable: false,
            },
            // {
            //   id: 'category',
            //   label: 'Category',
            //   defaultValue: content.category?.title ?? '—',
            //   editable: false,
            // },
            {
              id: 'description',
              label: 'Description',
              type: 'textarea',
              defaultValue: content.description,
            },
            {
              id: 'imageUrl',
              label: 'Thumbnail Image',
              type: 'file',
              accept: 'image/*',
              defaultValue: content.imageUrl || null,
            },
            ...(hasAudioSource
              ? [
                  {
                    id: 'audioName',
                    label: 'Audio Name',
                    defaultValue: content.audioKey?.split('/').pop() || 'No audio',
                    editable: false,
                  },
                  {
                    id: 'audioPreview',
                    label: 'Listen Audio',
                    type: 'custom' as const,
                    editable: false,
                    render: () => {
                      const audioSrc =
                        streamedAudioUrl || content.cachedAudioUrl || content.audioUrl || '';

                      return (
                        <audio key={audioSrc} controls className="w-full">
                          <source src={audioSrc} />
                          Your browser does not support audio.
                        </audio>
                      );
                    },
                  },
                  {
                    id: 'file',
                    label: 'Replace Audio',
                    type: 'file' as const,
                    accept: 'audio/*',
                    defaultValue: null,
                  },
                ]
              : []),
            ...(hasVideoSource
              ? [
                  {
                    id: 'videoFile',
                    label: 'Replace Video',
                    type: 'file' as const,
                    accept: 'video/mp4,video/webm,video/ogg,video/*',
                    defaultValue: content.videoUrl || null,
                  },
                ]
              : []),
            ...(hasEmbedSource
              ? [
                  {
                    id: 'embedUrl',
                    label: 'Replace Embed URL',
                    type: 'url' as const,
                    defaultValue: content.embedUrl || '',
                  },
                ]
              : []),
            // {
            //   id: 'duration',
            //   label: 'Duration',
            //   defaultValue: `${content.duration ?? 0} sec`,
            //   editable: false,
            // },
            // {
            //   id: 'playCount',
            //   label: 'Play Count',
            //   defaultValue: String(content.playCount ?? 0),
            //   editable: false,
            // },
          ]}
        />
      </div>
    </div>
  );
};
export default UnLockedContentDetail;
